import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  fetchAiModel,
  invokeAiModel,
  type AiModelInvokeResult,
  type StockChoice,
} from "../api/client";
import { ModelResultView } from "../components/ModelResultView";
import { StockStickerPicker } from "../components/StockStickerPicker";
import { TrainModelPromo } from "../components/TrainModelPromo";

export function ModelRunPage() {
  const { modelId } = useParams<{ modelId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const stockFromUrl = searchParams.get("stock")?.toUpperCase() ?? null;

  const [result, setResult] = useState<AiModelInvokeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stockChoices, setStockChoices] = useState<StockChoice[] | null>(null);
  const [selectedStock, setSelectedStock] = useState<string | null>(null);

  const run = useCallback(
    (stock: string | null) => {
      if (!modelId) return;
      setLoading(true);
      setError(null);
      const body = stock ? { stock } : undefined;
      invokeAiModel(modelId, body)
        .then(setResult)
        .catch(() => setError("We could not run this model right now. Please try again."))
        .finally(() => setLoading(false));
    },
    [modelId],
  );

  useEffect(() => {
    if (!modelId) {
      setError("Missing model id");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchAiModel(modelId)
      .then((meta) => {
        if (cancelled) return;
        const choices = meta.stock_choices ?? null;
        setStockChoices(choices);
        const fromUrl =
          stockFromUrl && choices?.some((c) => c.symbol === stockFromUrl)
            ? stockFromUrl
            : null;
        const initial = fromUrl ?? meta.default_stock ?? choices?.[0]?.symbol ?? null;
        setSelectedStock(initial);
        return invokeAiModel(modelId, initial ? { stock: initial } : undefined);
      })
      .then((invokeResult) => {
        if (!cancelled && invokeResult) setResult(invokeResult);
      })
      .catch(() => {
        if (!cancelled) setError("We could not run this model right now. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [modelId, stockFromUrl]);

  const onStockChange = (symbol: string) => {
    setSelectedStock(symbol);
    setSearchParams({ stock: symbol }, { replace: true });
    run(symbol);
  };

  if (!modelId) {
    return (
      <article className="page">
        <p className="eyebrow">AI Models</p>
        <h1>Something went wrong</h1>
        <p>Missing model id.</p>
        <Link to="/ai-models" className="card-link">
          ← Back to AI Models
        </Link>
      </article>
    );
  }

  return (
    <article className="page page--wide">
      {stockChoices && stockChoices.length > 0 && selectedStock && (
        <StockStickerPicker
          choices={stockChoices}
          value={selectedStock}
          disabled={loading}
          onChange={onStockChange}
        />
      )}

      {loading && <p className="muted page-loading">Running model…</p>}

      {error && !loading && (
        <p className="model-run-error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && result && (
        <ModelResultView result={result} onResultChange={setResult} />
      )}

      <TrainModelPromo compact />

      <Link to="/ai-models" className="card-link">
        ← Back to AI Models
      </Link>
    </article>
  );
}
